import React, { useRef } from 'react'
import {
  capitalize, Box, Button, Container, Typography, useTheme
} from '@mui/material'
import { Link as GatsbyLink } from 'gatsby'
import { useIntersectionObserver } from '@react-hookz/web/esnext'

import { useMobilePricingTabs } from './PricingCard'

import { Icon, getIcon } from '@components/Icon'
import { useHeaderHeight } from '@components/Header'
import { useForceClientRehydrationKey, useMediaQueries } from '~/hooks'
import { rudderslabTrackOnClick } from '~/utils/common'


// arbitrary
const COLUMN_WIDTH_TABLET = 140 
const COLUMN_WIDTH_LAPTOP = 208

type PricingColumn = {
  title: string
  linkText: string
  link: string
}

type ColumnHeadersProps = PricingColumn & {
  index: number
}
const ColumnHeader = ({
  index,
  title,
  linkText,
  link,
}: ColumnHeadersProps) => {
  const { isLaptopAndLarger } = useMediaQueries()
  return (
    <Box
      key={title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        p: 2,
        borderLeftWidth: index === 0 ? 0 : 1,
        borderColor: 'border.main',
        width: {
          tablet: COLUMN_WIDTH_TABLET,
          laptop: COLUMN_WIDTH_LAPTOP
        }
      }}>
      <Typography fontWeight={500} gutterBottom sx={{
        display: {
          xs: 'none',
          laptop: 'block'
        }
      }}>{title}</Typography>
      <Button
        component={link?.startsWith('http') ? 'a' : GatsbyLink}
        {...{
          [link?.startsWith('http') ? 'href' : 'to']: link
        }}
        onClick={e => rudderslabTrackOnClick('button', linkText, e)}
        variant="containedLight" sx={{
          display: 'flex',
          alignItems: 'center'
        }}>
        <Box component="span" sx={{ display: 'inline-block', mr: 1 }}>
          {isLaptopAndLarger ? linkText : title}
        </Box>
        <Icon icon="arrow-right" sx={{ ml: 'auto' }} />
      </Button>
    </Box>
  )
}

type ComparisonProps = {
  title: string
  pricing_columns: PricingColumn[]
  group_items: {
    group_items: {
      group_name: string
      icon: string
      table_data: {
        rows: {
          cells: string[]
        }[]
      }
    }[]
  }
}

export const Comparison = ({
  title,
  pricing_columns: pricingColumns,
  group_items: {
    group_items: groupItems
  }
}: ComparisonProps) => {
  const {palette} = useTheme()
  const [height] = useHeaderHeight('height')
  const rootRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersectionObserver(elementRef, { threshold: [0, 0.9] });
  const isClient = useForceClientRehydrationKey()

  return (
    <Box
      ref={rootRef}
      sx={{
        display: {
          xs: 'none',
          tablet: 'flex',
        },
        flexDirection: 'column',
        alignItems: 'center',
        mb: 12
      }}>
      <Container maxWidth="desktop" sx={{
        px: {
          xs: 0,
          laptop: 2
        }
      }}>
        <Box sx={{
          pt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderTopWidth: 1,
          borderColor: 'border.main',
        }}>
          <Typography ref={elementRef} variant="h2" fontWeight={500} sx={{ mb: 8 }}>
            {title}
          </Typography>
        </Box>
      </Container>
      <Box
        sx={{
          width: '100%',
          position: 'sticky',
          top: 0,
          zIndex: 1202, // just above top navigation
          bgcolor: 'background.default'
        }}>
        <Container maxWidth="desktop" sx={{
          px: {
            xs: 0,
            laptop: 2
          }
        }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              borderBottomWidth: 1,
              borderColor: 'border.main',
              height,
            }}>
            <Typography
              component="h2"
              variant="h6"
              sx={{
                flex: 1,
                pl: 2,
                display: 'flex',
                alignItems: 'center',
                transition: 'opacity .2s linear',
                opacity: {
                  xs: 0,
                  tablet: intersection?.isIntersecting ? 0 : 1,
                },
                fontSize: {
                  tablet: '1rem',
                  laptop: '1.25rem'
                }
              }}>
              {title}
            </Typography>
            {pricingColumns.map((column, index) => (
              <ColumnHeader
                key={column.title}
                {...column}
                index={index}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="desktop" sx={{
        px: {
          xs: 0,
          laptop: 2
        }
      }}>
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {groupItems.map((item) => (
            <>
              <Box key={item.group_name} sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                p: 2,
                borderBottomWidth: 1,
                borderColor: 'border.main'
              }}>
                <Icon icon={getIcon(item.icon)} sx={{mr: 2, fontSize: '18px'}} />
                <Typography fontWeight={500} fontSize="14px">{item.group_name}</Typography>
              </Box>
              {item.table_data.rows.map(({ cells }) => (
                <Box
                  key={`${cells[0]}-${cells[1]}`}
                  display="flex"
                  alignItems="stretch"
                  sx={{
                    borderBottomWidth: 1,
                    borderColor: 'border.main'
                  }}>
                  {cells.map((cell, i) => (
                    <Typography
                      key={`${isClient}-${cells[0]}-${i}`}
                      sx={{
                        p: 2,
                        fontSize: '14px',
                        width: {
                          tablet: COLUMN_WIDTH_TABLET,
                          laptop: COLUMN_WIDTH_LAPTOP
                        },
                        display: 'flex',
                        alignItems: 'center',
                        ...(i === 0 ? {
                          bgcolor: 'background.paper',
                          flex: 1
                        } : {
                          borderLeftWidth: i === 0 ? 0 : 1,
                          borderColor: 'border.main',
                        }),
                      }}>
                      {!cell && <Icon icon='minus' color={palette.text.disabled} fontSize='18px' />}
                      {cell === 'CHECK' ? <Icon icon="check-circle" fontSize="16px"/> : cell}
                    </Typography>
                  ))}
                </Box>
              ))}
            </>
          ))}
        </Box>
      </Container>
    </Box>
  )
}

/*
  THIS IS LESS THAN IDEAL BUT NEEDED TO BE DONE USING THE EXISTING SCHEMA
  The sanity schema uses table_data, meaning that the features are not
  coupled with the pricing columns directly. Therefore, we need to infer the
  pricing column based on the index of the rows in table_data
  Row @ index 0: feature name
  Row 1: Free
  Row 2: Starter
  Row 3: Growth
  Row 4: Enterprise
*/

export const ComparisonMobile = ({
  pricing_columns: pricingColumns,
  group_items: {
    group_items: groupItems
  }
}: ComparisonProps) => {
  const {palette} = useTheme()
  const [activeTab] = useMobilePricingTabs('tabs')
  const columnIndex = pricingColumns.findIndex(({ title }) => title === activeTab)
  
  if (activeTab === undefined || columnIndex === -1) {
    if (!activeTab) {
      console.warn('no active tab')
    } else {
      console.error(
        `No pricing_column title was found with the activeTab name ${activeTab}`,
        'pricing_column titles:',
        pricingColumns.map(({ title }) => title)
      )
    }
    return null
  }

  const planIndex = columnIndex + 1 // index 0 === feature name
  const currentPlanFeatures = groupItems.map(({ icon, group_name: groupName, table_data: td }) => ({
    groupIcon: icon,
    groupName,
    features: td.rows.map(({ cells }) => ({
      featureName: cells[0],
      featureValue: cells[planIndex]
    }))
  }))

  return (
    <Container>
      <Box sx={{
        display: {
          xs: 'flex',
          tablet: 'none'
        },
        flexDirection: 'column',
        alignItems: 'center',
        mt: 4,
        mb: 8
      }}>
        <Typography variant='h2' sx={{mb: 3}}>{capitalize(activeTab)} Plan Features</Typography>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          borderWidth: 1,
          borderColor: 'border.main',
          width: '100%'
        }}>
          {currentPlanFeatures.map(({ groupIcon, groupName, features }) => (
            <Box key={groupName} display="flex" flexDirection="column"
              sx={{
                borderColor: 'border.main',
                '&:not(:first-child)': {
                  borderTopWidth: 1,
                }
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  p: 2,
                  borderColor: 'border.main',
                  '&:not(:last-child)': {
                    borderBottomWidth: 1,
                  }
                }}>
                <Icon icon={getIcon(groupIcon)} sx={{ mr: 2, fontSize: '18px' }} />
                <Typography fontWeight={500} fontSize="14px">{groupName}</Typography>
              </Box>
              {features.map(({ featureName, featureValue }) => (
                <Box
                  key={groupName}
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    flex: 1,
                    borderColor: 'border.main',
                    '&:not(:last-child)': {
                      borderBottomWidth: 1
                    }
                  }}>
                  <Typography sx={{
                    flex: 1,
                    bgcolor: 'background.paper',
                    p: 2
                  }}>{featureName}</Typography>
                  <Typography
                    sx={{
                      width: 120,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    {!featureValue && <Icon icon='minus' color={palette.text.disabled} fontSize='18px' />}
                    {featureValue === 'CHECK' ? <Icon icon="check-circle" fontSize="16px" /> : featureValue}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  )
}